import React, { PropTypes, PureComponent } from 'react';
import SaveCancelModal from './SaveCancelModal';
import TextInput from '../inputs/TextInput';
import { GameStats, TYPES } from '../TourneyDataTree';
import _ from 'underscore';
import { GroupSelectWithDivision } from '../modals/AdminGameModal';
import NativeSelect from '../inputs/NativeSelect';
import { Icon, Tip } from 'react-fast-ui';
import LocationSearch from '../inputs/LocationSearch';
import FormControl from '../inputs/FormControl';
import cx from 'classnames';

const LatLngForm = ({ value, onChange }) => {
  const { latitude, longitude } = value || {};

  return (
    <ul className="list-unstyled display-flex mg-top-md">
      <li>
        <label>
          <span>Latitude:</span>
          <FormControl>
            <input
              type="number"
              value={latitude || ''}
              onChange={e => onChange({ ...value, latitude: parseFloat(e.target.value) })}
            />
          </FormControl>
        </label>
      </li>
      <li className="mg-left-md">
        <label>
          <span>Longitude:</span>
          <FormControl>
            <input
              type="number"
              value={longitude || ''}
              onChange={e => onChange({ ...value, longitude: parseFloat(e.target.value) })}
            />
          </FormControl>
        </label>
      </li>
    </ul>
  );
};

const TextGroup = ({ value, onChange, valueKey, label, ...rest }) => (
  <div {...rest}>
    <label>{label}</label>
    <TextInput
      placeholder={label}
      value={value ? value[ valueKey ] : ''}
      required
      onChange={e => onChange({ ...value, [ valueKey ]: e.target.value })}
    />
  </div>
);

const NameGroup = props => <TextGroup {...props} label="Name" valueKey="name"/>;
const NickNameGroup = props => <TextGroup {...props} label="Nickname" valueKey="nickname"/>;

const DivisionGroup = ({ value, onChange, divisions, ...rest }) => (
  <div {...rest}>
    <label>Division</label>
    <NativeSelect
      placeholder="Division" value={value ? value.division : null} required
      onChange={division => onChange({ ...value, division })} options={divisions}/>
  </div>
);

const STATS_TIP = <Tip tip="Calculated Wins/Ties/Losses Point Differential"><Icon name="question-circle"/></Tip>;

const StandingGroup = ({ value, onChange, className, ...rest }) => (
  <div className={cx('display-flex align-items-center', className)} {...rest}>
    <div className="flex-shrink-0 mg-right-md">
      <label>Rank</label>
      <div>
        <FormControl>
          <input
            placeholder="Rank" value={value && value.standing ? value.standing.rank : ''}
            min={1} max={20}
            style={{ width: 60 }}
            onChange={({ target: { value: rank } }) => onChange({
              ...value,
              standing: rank && rank.length ? { ...value.standing, rank } : null
            })}
          />
        </FormControl>
      </div>
    </div>
    <div className="flex-grow-1 mg-right-md">
      <label>Note</label>
      <div>
        <TextInput
          value={value && value.standing ? value.standing.note : ''}
          placeholder="Rank Note"
          disabled={!(value && value.standing)}
          onChange={({ target: { value: note } }) => onChange({
            ...value,
            standing: { ...value.standing, note }
          })}
        />
      </div>
    </div>
    <div className="flex-shrink-0">
      <label>Stats {STATS_TIP}</label>
      <div>
        {value.standing && value.standing.stats ? <GameStats stats={value.standing.stats}/> : <strong>N/A</strong>}
      </div>
    </div>
  </div>
);

const VenueGroup = ({ value, onChange, venues, ...rest }) => (
  <div {...rest}>
    <label>Venue</label>

    <NativeSelect
      placeholder="Venue" value={value ? value.venue : null} required
      onChange={venue => onChange({ ...value, venue })}
      options={venues}/>
  </div>
);

const PoolGroup = ({ value, onChange, pools, ...rest }) => (
  <div {...rest}>
    <label>Pool</label>
    <GroupSelectWithDivision
      placeholder="Pool" value={value ? value.pool : null}
      required onChange={pool => onChange({ ...value, pool })}
      options={pools}/>
  </div>
);

const AddressGroup = ({ value, onChange, ...rest }) => {
  const location = (value && value.location) ? value.location : null;
  const handleChange = location => onChange({ ...value, location: location ? _.omit(location, 'name') : null });

  return (
    <div {...rest}>
      <label>Location</label>
      <LocationSearch value={location} clearable={false} onChange={handleChange}/>
      <LatLngForm
        value={location ? location.latLng : null}
        onChange={latLng => handleChange({ latLng })}
      />
    </div>
  );
};

export class NameModal extends PureComponent {
  static propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired
  };

  render() {
    const { value, onChange, ...rest } = this.props;

    return (
      <SaveCancelModal
        {...rest}>
        <NameGroup value={value} onChange={onChange}/>
      </SaveCancelModal>
    );
  }
}

export class GroupModal extends PureComponent {
  static propTypes = {
    ...NameModal.propTypes
  };

  render() {
    const { value, onChange, data: { [ TYPES.division ]: divisions }, ...rest } = this.props;

    return (
      <SaveCancelModal
        {...rest}>
        <NameGroup value={value} onChange={onChange} className="mg-bottom-sm"/>
        <DivisionGroup value={value} onChange={onChange} divisions={divisions}/>
      </SaveCancelModal>
    );
  }
}

export class TeamModal extends PureComponent {
  static propTypes = {
    ...NameModal.propTypes
  };

  render() {
    const { value, onChange, data: { [ TYPES.group ]: groups }, ...rest } = this.props;

    return (
      <SaveCancelModal
        {...rest}>
        <NameGroup value={value} onChange={onChange} className="mg-bottom-sm"/>
        <PoolGroup
          value={value}
          onChange={onChange}
          pools={_.filter(groups, ({ type }) => type === 'Pool')}
          className="mg-bottom-sm"
        />
        <StandingGroup value={value} onChange={onChange}/>
      </SaveCancelModal>
    );
  }
}

export class VenueModal extends PureComponent {
  static propTypes = {
    ...NameModal.propTypes
  };

  render() {
    const { value, onChange, ...rest } = this.props;

    return (
      <SaveCancelModal
        {...rest}>
        <NameGroup value={value} onChange={onChange} className="mg-bottom-sm"/>
        <NickNameGroup value={value} onChange={onChange} className="mg-bottom-sm"/>
        <AddressGroup value={value} onChange={onChange}/>
      </SaveCancelModal>
    );
  }
}

export class CourtModal extends PureComponent {
  render() {
    const { value, onChange, data: { [ TYPES.venue ]: venues }, ...rest } = this.props;

    return (
      <SaveCancelModal
        {...rest}>
        <NameGroup value={value} onChange={onChange} className="mg-bottom-sm"/>
        <VenueGroup value={value} onChange={onChange} venues={venues}/>
      </SaveCancelModal>
    );
  }
}


// WEBPACK FOOTER //
// ./src/js/components/modals/TourneyStructureEditModals.jsx